import debounce from 'lodash/debounce'
import { useCallback, useEffect, useState } from 'react'

import { TaggedUserType } from '@avantstay/arriere-clients/dist/arriereBackoffice'

import { GetOptionsCallbackProps, SugestionProps } from './MentionUser.types'

export type TaggedUser = { id: string; type: TaggedUserType }

interface UseCommentTaggingProps {
  workOrderId: string | undefined
  getCommentTagOptions: (
    query: string,
  ) => Promise<Array<{ id: string; name: string; type: TaggedUserType }>>
}

interface UseCommentTaggingReturn {
  comment: string
  usersTagged: string[]
  userTypeMap: Map<string, TaggedUserType>
  setComment: (comment: string) => void
  handleChange: (
    event: { target: { value: string } },
    newValue: string,
    newPlainTextValue: string,
    mentions: SugestionProps[],
  ) => void
  handleGetUsers: (query: string | undefined, callback: GetOptionsCallbackProps) => void
  resetComment: () => void
}

export const useCommentTagging = ({
  workOrderId,
  getCommentTagOptions,
}: UseCommentTaggingProps): UseCommentTaggingReturn => {
  const [comment, setComment] = useState('')
  const [usersTagged, setUsersTagged] = useState<string[]>([])
  const [userTypeMap, setUserTypeMap] = useState<Map<string, TaggedUserType>>(new Map())

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleGetUsers = useCallback(
    debounce((query: string | undefined, callback: GetOptionsCallbackProps) => {
      if (!query) return
      getCommentTagOptions(query)
        .then(result =>
          result.map(user => {
            setUserTypeMap(prevState => new Map(prevState).set(String(user.id), user.type))
            return { id: user.id, display: user.name }
          }),
        )
        .then(callback)
    }, 300),
    [],
  )

  const handleChange = (
    event: { target: { value: string } },
    _newValue: string,
    _newPlainTextValue: string,
    mentions: SugestionProps[],
  ) => {
    setUsersTagged(mentions.map(user => String(user.id)))
    setComment(event.target.value ?? '')
  }

  const resetComment = () => {
    setComment('')
    setUsersTagged([])
  }

  useEffect(() => {
    resetComment()
  }, [workOrderId])

  return {
    comment,
    usersTagged,
    userTypeMap,
    setComment,
    handleChange,
    handleGetUsers,
    resetComment,
  }
}
